import {logger, Util} from 'yougile-util'
import {DataObject, DataObjectId, DataObjectJSON} from './data-object'

export interface ConnectionsJSON extends DataObjectJSON {
  from: DataObjectId
  to: {[key: string]: number}
  modLists: {[key: string]: string[]}
}

export const isConnectionsJSON = (
  obj: DataObjectJSON
): obj is ConnectionsJSON => {
  return !!(obj as ConnectionsJSON)?.from && obj?.dataType === 'Connections'
}

export class Connections extends DataObject {
  public from: DataObjectId

  // toId -> timestamp
  private changed: Map<DataObjectId | 'all', number> = new Map()
  private to: Set<DataObjectId> = new Set()
  private modLists: Map<DataObjectId, string[]> = new Map()

  constructor(companyId: DataObjectId, fromId: DataObjectId) {
    super(companyId)
    this.dataType = 'Connections'
    this.from = fromId
    this.isLazy = false
  }

  public connect(to: DataObjectId): void {
    this.to.add(to)
    this.changed.set(to, Date.now())
  }

  public disconnect(to: DataObjectId): void {
    this.to.delete(to)
    this.modLists.delete(to)
    this.changed.set(to, Date.now())
  }

  public disconnectAll(): void {
    this.to.clear()
    this.modLists.clear()
    this.changed.set('all', Date.now())
  }

  public getModifiers(to: DataObjectId): string[] | undefined {
    return this.modLists.get(to)
  }

  public isConnected(to: DataObjectId): boolean {
    return this.to.has(to)
  }

  public getAllConnected(): Set<DataObjectId> {
    return this.to
  }

  public addModifiers(to: DataObjectId, modifiers: string[]): void {
    modifiers.forEach(m => {
      const mod = this.modLists.get(to)
      if (mod?.includes(m)) {
        // already added, skipping…
        return
      }
      const toList = this.modLists.get(to)
      if (!toList) {
        this.modLists.set(to, [m])
      } else {
        toList.push(m)
      }
    })
    this.changed.set(to, Date.now())
  }

  public removeModifiers(to: DataObjectId, modifiers: string[]): void {
    modifiers.forEach((m: string) => {
      const list = this.modLists.get(to)
      if (list) {
        const fix = list.indexOf(m)
        list.splice(fix, 1)
      }
    })
    this.changed.set(to, Date.now())
  }

  public setModifiers(to: DataObjectId, modifiers: string[]): void {
    this.modLists.set(to, modifiers)
    this.changed.set(to, Date.now())
  }

  private clearOldChanged(): void {
    const now = Date.now()
    const ttl = 5 * 3600_000
    ;[...this.changed].forEach(entry => {
      if (!entry[1] || entry[1] - now > ttl) {
        logger.log('Remove old changed', this.from, entry[0])
        this.changed.delete(entry[0])
      }
    })
  }

  public getChanged(): DataObjectId[] | 'all' {
    this.clearOldChanged()
    if (this.changed.has('all')) {
      return 'all'
    }
    return [...this.changed.keys()] as DataObjectId[]
  }

  public override toJSON(): ConnectionsJSON {
    const res = super.toJSON() as ConnectionsJSON
    res.from = this.from
    res.to = Util.serializeStringSet(this.to)
    res.modLists = Util.serializeStringMap(this.modLists)
    return res
  }

  public override fromJSON(
    companyId: DataObjectId,
    json: ConnectionsJSON
  ): Connections {
    super.fromJSON(companyId, json)
    this.from = json.from
    this.to = Util.deserializeStringSet(json.to) as Set<DataObjectId>
    this.modLists = Util.deserializeStringMap(json.modLists) as Map<
      DataObjectId,
      string[]
    >
    return this
  }

  public add(to: DataObjectId): void {
    if (!to || !Util.isIdOrUUID(to)) {
      throw new Error('Incorrect "to" value')
    }
    this.to.add(to)
    this.changed.set(to, Date.now())
    this.commit()
  }

  public remove(to: DataObjectId): void {
    this.to.delete(to)
    this.changed.set(to, Date.now())
    this.commit()
  }
}
